<script lang="ts" setup>
import { computed, type PropType, toRef } from 'vue'
import { storeToRefs } from 'pinia'

import { FavoriteButton } from '@haskoning/vue-component-library'
import CrossSectionDetailTable from '@/components/calculation-result/CrossSectionDetailTable.vue'
import FlowDataTable from '@/components/calculation-result/FlowDataTable.vue'
import CrossSectionImages from '@/components/calculation-result/CrossSectionImages.vue'
import CostDataTable from '@/components/calculation-result/CostDataTable.vue'

import type { CrossSectionType } from '@/stores/type/crossSection.type'
import { formatValue } from '@/components/calculation-result/utils/formatValue'

import { useCrossSectionStore } from '@/stores/crossSection'

const crossSectionStore = useCrossSectionStore()

const { availableInfo, fields } = storeToRefs(crossSectionStore)

const props = defineProps({
  item: { type: Object as PropType<CrossSectionType>, required: true }
})

const open = ref(false)
const exportExcelLoading = ref(false)
const exportMathCADLoading = ref(false)

const item = toRef(props, 'item')

const KEY: (keyof CrossSectionType)[] = [
  'calculation_method',
  'calculation_type',
  'vessel_name',
  'normative_flow_velocity_at_bed',
  'normative_total_area',
  'normative_min_required_grading',
  'normative_effective_safety_factor',
  'normative_average_rock_layer_thickness',
  'normative_d_blockmat',
  'normative_d_block'
]

const availableKeys = computed<(keyof CrossSectionType)[]>(() => {
  return KEY.filter((key) => availableInfo.value.some((info) => key.includes(info)))
})

const getChoices = (key: keyof CrossSectionType) => {
  return fields.value ? (fields.value[key]?.choices ?? []) : []
}

const getValue = (key: keyof CrossSectionType): string => {
  const choiceField = ['calculation_method', 'calculation_type', 'normative_min_required_grading']
  if (choiceField.includes(key)) {
    return getChoices(key).find((choice) => choice.value === item.value[key])?.display_name ?? ''
  }
  return formatValue(item.value[key])
}

const isNumberColumn = (key: keyof CrossSectionType): boolean => {
  return [
    'normative_flow_velocity_at_bed',
    'normative_total_area',
    'normative_effective_safety_factor',
    'normative_average_rock_layer_thickness',
    'normative_d_blockmat',
    'normative_d_block'
  ].includes(key)
}

function toggleOpen(): void {
  if (item.value) {
    open.value = !open.value
  }
}

const downloadExcel = async (id: number): Promise<void> => {
  if (item.value) {
    exportExcelLoading.value = true
    await crossSectionStore.downloadExcel(id)
    exportExcelLoading.value = false
  }
}
const downloadMathCAD = async (id: number): Promise<void> => {
  if (item.value) {
    exportMathCADLoading.value = true
    await crossSectionStore.downloadMathCAD(id)
    exportMathCADLoading.value = false
  }
}

const updateFavorite = async () => {
  if (item.value) {
    item.value.favorite = !item.value.favorite
    await crossSectionStore.update(item.value)
  }
}
</script>

<template>
  <tr v-if="item" v-ripple>
    <td v-for="key in availableKeys" :class="[isNumberColumn(key) ? 'text-center' : '']" :key="key">
      {{ getValue(key) }}
    </td>
    <td>
      <FavoriteButton :item="item" @update-favorite="updateFavorite"></FavoriteButton>
    </td>
    <td class="text-center">
      <v-btn
        icon="mdi-table-arrow-down"
        class="my-1"
        variant="text"
        :loading="exportExcelLoading"
        @click.stop="downloadExcel(item.id)"
      >
      </v-btn>
    </td>
    <td class="text-center">
      <v-btn
        icon="mdi-folder-arrow-down-outline"
        class="my-1"
        variant="text"
        :loading="exportMathCADLoading"
        @click.stop="downloadMathCAD(item.id)"
      >
      </v-btn>
    </td>
    <td>
      <v-btn
        :icon="open ? `mdi-chevron-up` : `mdi-chevron-right`"
        size="40"
        class="my-1"
        variant="text"
        @click.stop="toggleOpen"
      >
      </v-btn>
    </td>
  </tr>
  <tr v-if="item">
    <td v-if="open" class="pl-0 pr-0" colspan="999" @click.stop="">
      <v-divider></v-divider>
      <v-row no-gutters>
        <v-col cols="4">
          <FlowDataTable :item="item"> </FlowDataTable>
          <v-divider></v-divider>
          <CostDataTable :item="item"> </CostDataTable>
        </v-col>
        <v-divider vertical></v-divider>
        <v-col cols="8">
          <CrossSectionDetailTable :item="item"></CrossSectionDetailTable>
        </v-col>
      </v-row>

      <CrossSectionImages />

      <v-divider :thickness="2"></v-divider>
    </td>
  </tr>
</template>
